import React, {useMemo} from 'react';
import {connect} from 'react-redux';
import SMCPage from '../../../components/core/pageComponents/SMCPage';
import {useResultsTableDimensions, useBoolean, useToggle} from '../../../utils/hooks';
import SMCTable from '../../../components/core/SMCTable/SMCTable';
import {useFirmwareResults} from './firmwareResultsHelper';
import {isUserAdminSelector, firmwarePermissionsSelector, isAdminReadOnlySelector} from '../../../store/selectors/user.selectors';
import FirmwareWizard from '../../Wizards/FirmwareWizard';
import RecommandFirmwareWizard from '../../Wizards/RecommandFirmwareWizard';
import FirmwareFilterPane from '../../Filters/FirmwareFilterPane';
import {helpContextPageUrl} from '../../../conf/Routes';
import SMCTableActions from '../../../components/core/SMCTableActions/SMCTableActions';
import {FirmwareFilterSchema} from '../../../api/DBSchema/Firmware';

function FirmwareResults({
    isUserAdmin,
    isAdminReadOnly,
    firmwarePermissions
}) {
    
    const [tableWidth, tableHeight] = useResultsTableDimensions();
    const [isNewObjectWindowOpen, openNewObjectWindow, closeNewObjectWindow] = useBoolean(false);
    const [isRecommandedFirmwareWindowOpen, openRecommandedFirmwareWindow, closeRecommandedFirmwareWindow] = useBoolean(false);
    const [isFilterPaneOpen, toggleFilterPane] = useToggle(false);
    
    const {
        tableContent,
        isFetching,
        isPageLoaded,
        filterObject,
        setFilterObject,
        freeTextSearch,
        fetchNextPage,
        isLastPage,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        rowIdSelector,
        isRowSelectable,
        selectedObjects,
        setSelectedObjects,
        columnsWidths,
        setColumnsWidths,
        reloadButtonOnClick,
        deleteButtonOnClick
    } = useFirmwareResults();

    const canDelete = useMemo(() => {
        if (isUserAdmin) {
            return !isAdminReadOnly;
        }
        if (firmwarePermissions) {
            return !!firmwarePermissions.delete;
        }
        return false;
    }, [isUserAdmin, firmwarePermissions, isAdminReadOnly]);
    
    const canCreate = useMemo(() => {
        if (isUserAdmin) {
            return !isAdminReadOnly;
        }
        if (firmwarePermissions) {
            return !!firmwarePermissions.create;
        }
        return false;
    }, [isUserAdmin, firmwarePermissions, isAdminReadOnly]);

    return (
        <SMCPage loading={!isPageLoaded} helpId={helpContextPageUrl.firmwareResults}>
            <div className="DrawResultsDiv">
                <SMCTableActions
                    openNewObjectWindow={openNewObjectWindow}
                    openRecommandedFirmwareWindow={openRecommandedFirmwareWindow}
                    reloadButtonOnClick={reloadButtonOnClick}
                    deleteButtonOnClick={deleteButtonOnClick}
                    toggleFilterPane={toggleFilterPane}
                    canCreate={canCreate}
                    canDelete={canDelete}
                    isFetching={isFetching} 
                    tableContent={tableContent}
                    isFilterPaneOpen={isFilterPaneOpen}
                    selectedObjects={selectedObjects}
                    initialSearch={filterObject}
                    onSearchChange={setFilterObject}
                    searchAttribute={FirmwareFilterSchema.freeTextSearch}
                /> 
                <SMCTable
                    columns={columns}
                    columnsState={columnsState}
                    setColumnsState={setColumnsState}
                    setDefaultColumnsState={setDefaultColumnsState}
                    isFilterOpen={isFilterPaneOpen}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={setSelectedObjects}
                    hasCheckAll={true}
                    defaultColumnsWidths={columnsWidths}
                    onEndResizingColumns={setColumnsWidths}
                    content={tableContent}
                    sortBasis={sortBasis}
                    sortDirection={sortDirection}
                    setSort={setSort} 
                    loading={isFetching}
                    onRequestLoadPage={fetchNextPage}
                    isLastPage={isLastPage}
                    width={tableWidth}
                    height={tableHeight}
                    isRowSelectable={isRowSelectable}
                    rowIdSelector={rowIdSelector}
                    filterElem={
                        <FirmwareFilterPane
                            initialFilter={filterObject}
                            onFilterChange={setFilterObject}
                            open={isFilterPaneOpen}
                        />
                    }
                    textToHighlight={freeTextSearch}
                    showEditIcon={false}
                />
                {/* Floating windows */}
                <FirmwareWizard
                    isOpen={isNewObjectWindowOpen}
                    onRequestClose={closeNewObjectWindow}
                    onSuccess={() => { reloadButtonOnClick(); closeNewObjectWindow(); }}
                />
                <RecommandFirmwareWizard
                    isOpen={isRecommandedFirmwareWindowOpen}
                    onRequestClose={closeRecommandedFirmwareWindow}
                    onSuccess={closeRecommandedFirmwareWindow}
                />
            </div>
        </SMCPage>
    );
}

const mapStateToProps = state => ({
    isUserAdmin: isUserAdminSelector(state),
    isAdminReadOnly: isAdminReadOnlySelector(state),
    firmwarePermissions: firmwarePermissionsSelector(state)
});

const mapDispatchToProps = null;

export default connect(mapStateToProps, mapDispatchToProps)(FirmwareResults);